import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import {
  Dropdown,
  DropdownMenu,
  Badge,
  IconDisclosure,
  SearchInput,
} from "@toddle-edu/ds-web";
import {
  FolderFilled,
  SettingsOutlined,
  CalendarScheduleOutlined,
  FolderOutlined,
  PageLineUserOutlined,
} from "@toddle-edu/ds-icons";
import classes from "./SearchWithFilters.scss";
import { colors } from "Constants";
import DateRangeModal from "../DateRangeModal/DateRangeModal";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import useMediaQuery from "Hooks/UseMediaQuery";
import { TABLET_LANDSCAPE_MEDIA_QUERY } from "Constants/stringConstants";
import {
  isPortfolioParityEnabled,
  isPortfolioFilterRevamp,
  isPortfolioRevampAndIsJournalRoute,
  canManagePortfolioActions,
  showAcademicYearFilterProfilePortfolio,
  getIsEnableFamilyConsolidatedPortfolio,
  getShouldShowFolderCount,
  getIsSchedulePostTabEnabled,
} from "AppComponents/Post/utils";
import { ManageFoldersModal } from "Post/components";

const BLANK_OBJECT = {};
const BLANK_ARRAY = [];
const styles = {
  searchTextStyle: { paddingBottom: "16px" },
  carretIconStyle: { display: "flex" },
  folderFilledStyle: { color: colors.blue400 },
  manageFolderIconStyles: { padding: "2px", height: "auto", width: "auto" },
  noGapStyles: { gap: "0px" },
  containerStyle: {
    maxWidth: "600px",
  },
  combinedIconStyle: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  columnReverse: { flexDirection: "column-reverse" },
};

const SearchWithFilters = ({
  studentId,
  academicYearId,
  isStudentProfile,
  isArchivedClass,
  isFolderFilterDisabled,
  filters,
  courses,
  folders,
  updateFilterObject = _.noop,
  onManageFolderClicked,
  showFolderDropDown = true,
  showCourseDropDown = false,
  selectedAcademicYearIds = [],
  setSelectedAcademicYearIds = _.noop,
  containerStyle: containerStyleFromProp = {},
  searchContainerStyle = {},
  searchFilterContainerStyles: searchFilterContainerStylesFromProp = {},
  resetAcademicYearRef = null,
  journalFeedFilters,
  courseId,
  setCourseId,
  folderId: selectedFolder,
  setFolderId: setSelectedFolder,
  showAyYear = true,
  showSearchInSeparateRow = false,
}) => {
  const isMobile = useMediaQuery(TABLET_LANDSCAPE_MEDIA_QUERY);
  const { t } = useTranslation(["common", "journal", "schoolSetup"]);
  const userInfo = getUserInfo();
  const [selectedFolderV2, setSelectedFolderV2] = useState();
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [showManageFoldersModal, setShowManageFoldersModal] = useState(false);
  const [dateRangeData, setDateRangeData] = useState(BLANK_OBJECT);
  const [currectAcademicYear, setCurrentAcademicYear] = useState(BLANK_OBJECT);
  const dateFilterRef = useRef(null);
  const { user_type: userType } = userInfo;
  const subTextClasses = classNames(
    { ["text-subtitle-2"]: true },
    { [classes.searchSubText]: true }
  );
  const hasManageAccess = canManagePortfolioActions(userType);
  const showAcademicYearFilterInPortfolio =
    (showAcademicYearFilterProfilePortfolio() &&
      _.includes(window.location.href, "/studentFeed")) ||
    (getIsEnableFamilyConsolidatedPortfolio() &&
      _.includes(window.location.href, "/family-portfolio-summary"));

  useEffect(() => {
    const folderIds = _.get(filters, "folderIds");
    const setFolderIds = _.isFunction(setSelectedFolder)
      ? setSelectedFolder
      : setSelectedFolderV2;
    setFolderIds(() => {
      if (_.isEmpty(folderIds) && !isStudentProfile) {
        return "all_folders";
      } else if (_.isEmpty(folderIds) && isStudentProfile) {
        return "ALL";
      }
      return folderIds;
    });
  }, [filters]);

  const foldersSizeExcludingAll = useMemo(() => {
    return _.filter(folders, folder => folder.key !== "ALL").length;
  }, [folders]);
  const courseSizeExcludingAll = useMemo(() => {
    return _.filter(courses, course => course.key !== "ALL").length;
  }, [courses]);

  const appliedFilterCount = useMemo(() => {
    let hasDateFilters;
    const hasFolderIds = _.get(filters, "folderIds", null);
    const fromDate = _.get(filters, "duration.fromDate", null);
    const toDate = _.get(filters, "duration.toDate", null);

    if (isStudentProfile) {
      const selectedAcademicYearId = _.isArray(filters?.academicYears)
        ? _.get(filters, "academicYears[0]")
        : _.get(filters, "academicYears");

      hasDateFilters = !(
        selectedAcademicYearId == academicYearId &&
        _.isNull(fromDate) &&
        _.isNull(toDate)
      );
    } else {
      hasDateFilters = !_.isNull(fromDate) || !_.isNull(toDate);
    }

    return {
      calender: !showAyYear
        ? !(_.isNull(fromDate) && _.isNull(toDate))
        : hasDateFilters
        ? 1
        : 0,
      folders: !_.isNull(hasFolderIds) ? 1 : 0,
      courses: courseId ? 1 : 0,
    };
  }, [filters, currectAcademicYear, showAyYear]);

  const hasSearchText = !!_.get(filters, "searchText");

  const onSearchChange = value => {
    if (_.size(_.trim(value), 0) > 0) {
      updateFilterObject({ searchText: _.trimStart(value) });
    } else updateFilterObject({ searchText: undefined });
  };

  const manageFolderOption = {
    key: "manage_folder",
    value: "manage_folder_id",
    label: t("journal:manage_folders"),
    disabled: isArchivedClass,
    icon: (
      <SettingsOutlined size={isPortfolioParityEnabled() ? "xx-small" : ""} />
    ),
  };

  const getFolderOptions = () => {
    const shouldShowFolderCount = getShouldShowFolderCount();

    const allFoldersKey = isStudentProfile ? "ALL" : "all_folders";
    const allFoldersLabel = t("journal:all_folders");

    const FolderFilledIcon = <FolderFilled style={styles.folderFilledStyle} />;
    const options = [
      shouldShowFolderCount
        ? {
            key: allFoldersKey,
            label: (
              <div className={classes.foldersLabel}>
                {FolderFilledIcon}
                {allFoldersLabel}
              </div>
            ),
            value: "",
            color: "var(--blue-400)",
          }
        : {
            key: allFoldersKey,
            label: allFoldersLabel,
            value: "",
            color: "var(--blue-400)",
            icon: FolderFilledIcon,
          },
    ];

    _.forEach(folders, folder => {
      // Skip "ALL" folder as it's already added
      if (folder.key === "ALL" || folder.value === "ALL") {
        return;
      }

      const FolderFilledIcon = <FolderFilled style={{ color: folder.color }} />;

      shouldShowFolderCount
        ? options.push({
            key: isStudentProfile ? folder.key : folder.id,
            value: isStudentProfile ? folder.value : folder.id,
            label: (
              <div className={classes.foldersLabel}>
                {FolderFilledIcon}
                {isStudentProfile ? folder.label : folder.name}
              </div>
            ),
            color: folder.color,
            suffix: (
              <Badge
                dsVersion="2.0"
                value={folder.totalPostCount}
                shape="circle"
                variant="subtle"
              />
            ),
          })
        : options.push({
            key: folder.id,
            value: folder.id,
            label: folder.name,
            color: folder.color,
            icon: FolderFilledIcon,
          });
    });
    return options;
  };

  const folderSelected = data => {
    if (
      _.isEqual(data.folder, "all_folders") ||
      _.isEqual(data.folder, "ALL")
    ) {
      updateFilterObject({ folderIds: undefined });
      return;
    }
    updateFilterObject({ folderIds: data.folder ? data.folder : undefined });
  };

  const handleFolderItemClick = params => {
    setFolderDropDown(false);
    if (_.get(params, "key") === "manage_folder") {
      handleManageFolderClicked();
      return;
    }
    const setFolderIds = _.isFunction(setSelectedFolder)
      ? setSelectedFolder
      : setSelectedFolderV2;
    setFolderIds(_.get(params, "key"));
    folderSelected({ folder: _.get(params, "key") });
  };

  const handleCourseItemClick = params => {
    setCourseDropDown(false);
    setCourseId(_.get(params, "key"));
  };

  const onManageFolderModalClose = () => {
    setShowManageFoldersModal(false);
  };

  const handleManageFolderClicked = () => {
    if (isArchivedClass) return;
    if (isPortfolioParityEnabled()) {
      setShowManageFoldersModal(true);
    } else onManageFolderClicked?.();
  };

  const openDateRangeModal = () => setShowDateRangeModal(true);
  const closeDateRangeModal = () => setShowDateRangeModal(false);
  const [isFolderDropDownOpen, setFolderDropDown] = useState(false);
  const [isCourseDropDownOpen, setCourseDropDown] = useState(false);

  const updatedFiltersObject = useMemo(() => {
    const updatedFiltersObject = { ...filters };
    if (getIsSchedulePostTabEnabled()) {
      if (_.get(filters, "state") === "PUBLISHED") {
        if (userType === "staff") {
          updatedFiltersObject.states = ["PUBLISHED"];
        } else {
          updatedFiltersObject.states = ["PUBLISHED", "IN_APPROVAL"];
        }
        delete updatedFiltersObject.state;
      } else if (_.get(filters, "state") === "SCHEDULED") {
        if (userType === "staff") {
          updatedFiltersObject.states = ["SCHEDULED"];
        } else {
          updatedFiltersObject.states = ["SCHEDULED", "IN_APPROVAL"];
        }
        delete updatedFiltersObject.state;
      }
    } else {
      if (_.get(filters, "state") === "PUBLISHED") {
        if (userType === "staff") {
          updatedFiltersObject.states = ["PUBLISHED", "SCHEDULED"];
        } else {
          updatedFiltersObject.states = ["PUBLISHED", "IN_APPROVAL"];
        }
        delete updatedFiltersObject.state;
      }
    }
    return updatedFiltersObject;
  }, [filters]);

  const folderOptions = getShouldShowFolderCount()
    ? getFolderOptions()
    : isStudentProfile
    ? folders
    : getFolderOptions();

  const CombinedIcons = props => {
    const { icon, showBadge } = props;
    return (
      <span style={styles.combinedIconStyle}>
        {icon}
        {showBadge ? (
          <Badge
            value={1}
            shape="circle"
            dsVersion="2.0"
            variant="dark"
            size={"xxx-small"}
          />
        ) : null}
      </span>
    );
  };

  const toggleDateRangeModal = e => {
    if (showDateRangeModal) {
      closeDateRangeModal();
    } else {
      openDateRangeModal();
    }
    e.stopPropagation();
  };

  const toggleFolderDropdown = value => {
    setFolderDropDown(value);
  };

  const toggleCourseDropdown = value => {
    setCourseDropDown(value);
  };

  const updatedContainerStyle = {
    ...(isPortfolioRevampAndIsJournalRoute()
      ? styles.containerStyle
      : BLANK_OBJECT),
    ...(showAcademicYearFilterInPortfolio
      ? containerStyleFromProp
      : BLANK_OBJECT),
  };

  const getSearchTextStyles = () => {
    if (!hasSearchText) {
      return styles.searchTextStyle;
    }
    return BLANK_OBJECT;
  };

  const searchFilterContainerStyles = {
    ...getSearchTextStyles(),
    ...searchFilterContainerStylesFromProp,
    ...(showSearchInSeparateRow ? styles.columnReverse : BLANK_OBJECT),
  };

  const shouldShowFolderCount = getShouldShowFolderCount();
  const minWidthDropDown = { minWidth: "200px" };

  return (
    <div className={classes.container} style={updatedContainerStyle}>
      <div
        style={searchFilterContainerStyles}
        className={classes.searchAndFiltersContainerV2}
      >
        {/* Search box */}
        <div
          className={
            showAcademicYearFilterInPortfolio
              ? classes.searchContainerV2
              : classes.searchContainer
          }
          style={searchContainerStyle}
        >
          <SearchInput
            dsVersion="2.0"
            size="large"
            placeholder={t("journal:search_posts")}
            value={_.get(filters, "searchText", "")}
            onChange={onSearchChange}
            focusSearchBySlash={isMobile ? false : true}
          />
        </div>
        <div
          className={classNames(
            classes.dateAndFolderContainer,
            showSearchInSeparateRow &&
              classes.dateAndFolderContainerInSeparateRow
          )}
        >
          {showCourseDropDown ? (
            <Dropdown
              dsVersion="2.0"
              placement="bottomRight"
              onVisibleChange={toggleCourseDropdown}
              overlay={
                <DropdownMenu
                  dsVersion="2.0"
                  value={courseId}
                  showSelection
                  selectionType="radio"
                  options={courseSizeExcludingAll > 0 ? courses : BLANK_ARRAY}
                  noResultLabel={t("schoolSetup:no_classes_assigned")}
                  onClick={handleCourseItemClick}
                />
              }
            >
              <div className={classes.iconMenu}>
                <IconDisclosure
                  size={"large"}
                  variant={"neutral"}
                  type={"outlined"}
                  isActivated={isCourseDropDownOpen}
                  icon={
                    <CombinedIcons
                      icon={
                        <PageLineUserOutlined
                          variant="neutral"
                          type="outlined"
                        />
                      }
                      showBadge={!!appliedFilterCount?.courses}
                    />
                  }
                  dsVersion="2.0"
                ></IconDisclosure>
              </div>
            </Dropdown>
          ) : null}
          <div
            ref={dateFilterRef}
            className={classes.iconMenu}
            onClick={toggleDateRangeModal}
          >
            <div className={classes.iconMenu}>
              <IconDisclosure
                size={"large"}
                variant={"neutral"}
                type={"outlined"}
                isActivated={showDateRangeModal}
                icon={
                  <CombinedIcons
                    icon={
                      <CalendarScheduleOutlined
                        variant="neutral"
                        type="outlined"
                        onClick={toggleDateRangeModal}
                      />
                    }
                    showBadge={!!appliedFilterCount?.calender}
                  />
                }
                dsVersion="2.0"
              ></IconDisclosure>
            </div>
          </div>
          {showFolderDropDown ? (
            <Dropdown
              dsVersion="2.0"
              disabled={isFolderFilterDisabled}
              placement="bottomRight"
              onVisibleChange={toggleFolderDropdown}
              overlayStyle={shouldShowFolderCount ? minWidthDropDown : {}}
              overlay={
                <DropdownMenu
                  dsVersion="2.0"
                  value={selectedFolder ? selectedFolder : selectedFolderV2}
                  showSelection
                  selectionType="radio"
                  staticOption={
                    !isStudentProfile && userType == "staff" && hasManageAccess
                      ? manageFolderOption
                      : undefined
                  }
                  options={
                    foldersSizeExcludingAll > 0 ? folderOptions : BLANK_ARRAY
                  }
                  noResultLabel={t("journal:no_folders_created_yet_in_class")}
                  onClick={handleFolderItemClick}
                />
              }
            >
              <div className={classes.iconMenu}>
                <IconDisclosure
                  size={"large"}
                  variant={"neutral"}
                  type={"outlined"}
                  isActivated={isFolderDropDownOpen}
                  icon={
                    <CombinedIcons
                      icon={
                        <FolderOutlined variant="neutral" type="outlined" />
                      }
                      showBadge={!!appliedFilterCount?.folders}
                    />
                  }
                  dsVersion="2.0"
                ></IconDisclosure>
              </div>
            </Dropdown>
          ) : null}
          {!isStudentProfile && isPortfolioFilterRevamp() && (
            <ManageFoldersModal
              isOpen={showManageFoldersModal}
              courseFolders={folders}
              onClose={onManageFolderModalClose}
            />
          )}
        </div>
        <DateRangeModal
          open={showDateRangeModal}
          showNewDesign={!isStudentProfile}
          studentId={studentId}
          academicYearId={academicYearId}
          isStudentProfile
          dateRangeData={dateRangeData}
          onDateChange={setDateRangeData}
          hasAcademicYear={isStudentProfile}
          filters={updatedFiltersObject}
          journalFeedFilters={journalFeedFilters}
          updateFilterObject={updateFilterObject}
          updateCurrentAcademcYear={setCurrentAcademicYear}
          onCloseClick={closeDateRangeModal}
          dateFilterRef={dateFilterRef}
          onClosePopOver={closeDateRangeModal}
          selectedAcademicYearIds={selectedAcademicYearIds}
          setSelectedAcademicYearIds={setSelectedAcademicYearIds}
          resetAcademicYearRef={resetAcademicYearRef}
          setCourseId={setCourseId}
          showAyYear={showAyYear}
        />
      </div>
      {hasSearchText && (
        <div className={subTextClasses}>
          {t("journal:showing_results_for", {
            searchText: _.get(filters, "searchText"),
          })}
        </div>
      )}
    </div>
  );
};

const arePropsEqual = (prevProps, nextProps) => {
  const prevDuration = _.get(prevProps.filters, "duration");
  const nextDuration = _.get(nextProps.filters, "duration");
  const prevShowCourseDropDown = _.get(prevProps, "showCourseDropDown");
  const nextShowCourseDropDown = _.get(nextProps, "showCourseDropDown");
  const prevShowSearchInSeparateRow = _.get(
    prevProps,
    "showSearchInSeparateRow"
  );
  const nextShowSearchInSeparateRow = _.get(
    nextProps,
    "showSearchInSeparateRow"
  );

  const isDurationEqual =
    _.isEqual(prevDuration, nextDuration) ||
    (_.isNull(prevDuration?.fromDate) &&
      _.isNull(nextDuration?.fromDate) &&
      _.isNull(prevDuration?.toDate) &&
      _.isNull(nextDuration?.toDate));

  const prevFiltersWithoutDuration = _.omit(prevProps.filters, "duration");
  const nextFiltersWithoutDuration = _.omit(nextProps.filters, "duration");

  const isFiltersEqual =
    _.isEqual(prevFiltersWithoutDuration, nextFiltersWithoutDuration) &&
    isDurationEqual &&
    !(_.isNull(prevDuration) && _.isNull(nextDuration));

  const otherPropsEqual =
    prevProps.studentId === nextProps.studentId &&
    prevProps.academicYearId === nextProps.academicYearId &&
    prevProps.isStudentProfile === nextProps.isStudentProfile &&
    prevProps.isArchivedClass === nextProps.isArchivedClass &&
    prevProps.isFolderFilterDisabled === nextProps.isFolderFilterDisabled &&
    prevProps.showFolderDropDown === nextProps.showFolderDropDown &&
    _.isEqual(prevProps.folders, nextProps.folders) &&
    prevProps.updateFilterObject === nextProps.updateFilterObject &&
    prevProps.onManageFolderClicked === nextProps.onManageFolderClicked &&
    prevShowCourseDropDown === nextShowCourseDropDown &&
    prevShowSearchInSeparateRow === nextShowSearchInSeparateRow;

  return isFiltersEqual && otherPropsEqual;
};

export default React.memo(SearchWithFilters, arePropsEqual);
SearchWithFilters.displayName = "SearchWithFilters";
